import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/lib/toast";
import {
  Archive,
  Ban,
  Check,
  CheckCheck,
  ChevronDown,
  Clock3,
  Download,
  FileText,
  Loader2,
  MessageSquareText,
  Music,
  Paperclip,
  RefreshCcw,
  SendHorizontal,
  UsersRound,
  X,
} from "lucide-react";

interface WhatsAppChatPanelProps {
  empresaId: number | null;
  isEmpresaLoading: boolean;
  instanceName: string | null;
  isConnected: boolean;
}

interface MessageKey {
  id: string;
  fromMe: boolean;
  remoteJid: string;
  participant?: string;
  remoteJidAlt?: string;
  addressingMode?: string;
}

interface ChatContact {
  jid: string;
  name: string;
  number: string;
  profilePicUrl: string | null;
  lastMessage: string | null;
  lastTimestamp: string | null;
  unreadCount: number;
  isGroup: boolean;
  lastMessageKey: MessageKey | null;
}

interface ChatMessage {
  id: string;
  jid: string;
  fromMe: boolean;
  text: string;
  timestamp: string | null;
  hasMedia: boolean;
  mediaType: "audio" | "image" | "video" | "document" | "sticker" | "unknown" | null;
  mimeType: string | null;
  fileName: string | null;
  caption: string | null;
  deliveryStatus: "pending" | "sent" | "delivered" | "read" | "played" | "error" | "unknown" | null;
  statusRaw: string | null;
  key: MessageKey | null;
}

interface MessageMediaState {
  isLoading: boolean;
  dataUrl: string | null;
  mimeType: string | null;
  error: string | null;
}

interface PendingAttachment {
  base64: string;
  mediatype: "image" | "video" | "document" | "audio";
  mimeType: string;
  fileName: string;
  previewUrl: string | null;
}

const CHAT_POLLING_INTERVAL_MS = 5_000;

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
};

const MEDIA_TYPES = ["audio", "image", "video", "document", "sticker", "unknown"] as const;
type MediaType = (typeof MEDIA_TYPES)[number];

const DELIVERY_STATUSES = ["pending", "sent", "delivered", "read", "played", "error", "unknown"] as const;
type DeliveryStatus = (typeof DELIVERY_STATUSES)[number];

const isMediaType = (value: unknown): value is MediaType => {
  return typeof value === "string" && (MEDIA_TYPES as readonly string[]).includes(value);
};

const isDeliveryStatus = (value: unknown): value is DeliveryStatus => {
  return typeof value === "string" && (DELIVERY_STATUSES as readonly string[]).includes(value);
};

const MEDIA_PLACEHOLDER_TEXTS = new Set(["[Áudio]", "[Imagem]", "[Vídeo]", "[Documento]", "[Sticker]", "[Mídia]"]);

const MESSAGE_SCROLL_BOTTOM_THRESHOLD = 36;

const MEDIA_REQUEST_TIMEOUT_MS = 25_000;
const CONTACTS_CACHE_TTL_MS = 45_000;

const normalizeBase64 = (value: string) => {
  return value.replace(/\s+/g, "").replace(/-/g, "+").replace(/_/g, "/");
};

const looksLikeBase64 = (value: string) => {
  const compact = normalizeBase64(value);
  if (compact.length < 80) return false;
  return /^[A-Za-z0-9+/=]+$/.test(compact);
};

const toMediaDataUrl = (value: string, mimeType: string | null) => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith("data:") || trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    return trimmed;
  }

  if (!looksLikeBase64(trimmed)) return null;
  return `data:${mimeType || "application/octet-stream"};base64,${normalizeBase64(trimmed)}`;
};

const extractMimeTypeFromPayload = (payload: unknown, depth = 0): string | null => {
  if (depth > 6 || !payload) return null;

  if (typeof payload === "string") {
    const parsed = payload.trim();
    return parsed.includes("/") ? parsed : null;
  }

  if (Array.isArray(payload)) {
    for (const item of payload) {
      const nested = extractMimeTypeFromPayload(item, depth + 1);
      if (nested) return nested;
    }
    return null;
  }

  if (!isRecord(payload)) return null;

  const directCandidates = [payload.mimeType, payload.mimetype, payload.contentType, payload.type];
  for (const candidate of directCandidates) {
    if (typeof candidate === "string" && candidate.trim().includes("/")) {
      return candidate.trim();
    }
  }

  const nestedCandidates = [payload.result, payload.payload, payload.data, payload.message, payload.media, payload.file];
  for (const candidate of nestedCandidates) {
    const nested = extractMimeTypeFromPayload(candidate, depth + 1);
    if (nested) return nested;
  }

  return null;
};

const extractMediaContentFromPayload = (payload: unknown, depth = 0): string | null => {
  if (depth > 6 || !payload) return null;

  if (typeof payload === "string") {
    const trimmed = payload.trim();
    if (!trimmed) return null;
    if (trimmed.startsWith("data:") || trimmed.startsWith("http://") || trimmed.startsWith("https://")) return trimmed;
    return looksLikeBase64(trimmed) ? trimmed.replace(/\s+/g, "") : null;
  }

  if (Array.isArray(payload)) {
    for (const item of payload) {
      const nested = extractMediaContentFromPayload(item, depth + 1);
      if (nested) return nested;
    }
    return null;
  }

  if (!isRecord(payload)) return null;

  const directCandidates = [payload.base64, payload.data, payload.file, payload.buffer, payload.media, payload.url, payload.src];
  for (const candidate of directCandidates) {
    const nested = extractMediaContentFromPayload(candidate, depth + 1);
    if (nested) return nested;
  }

  const nestedCandidates = [payload.result, payload.payload, payload.message, payload.mediaMessage];
  for (const candidate of nestedCandidates) {
    const nested = extractMediaContentFromPayload(candidate, depth + 1);
    if (nested) return nested;
  }

  return null;
};

const patchDataUrlMimeType = (dataUrl: string, mimeType: string | null, mediaType: MediaType | null): string => {
  if (!dataUrl.startsWith("data:application/octet-stream;base64,")) return dataUrl;

  const fallbackMimeTypeByMediaType: Record<Exclude<MediaType, "unknown">, string> = {
    image: "image/jpeg",
    audio: "audio/ogg",
    video: "video/mp4",
    document: "application/pdf",
    sticker: "image/webp",
  };

  const resolvedMimeType =
    (mimeType && mimeType.includes("/") ? mimeType : null) ||
    (mediaType && mediaType !== "unknown" ? fallbackMimeTypeByMediaType[mediaType] : null);

  if (!resolvedMimeType) return dataUrl;
  return dataUrl.replace("data:application/octet-stream;base64,", `data:${resolvedMimeType};base64,`);
};

const isViewportNearBottom = (viewport: HTMLDivElement) => {
  return viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight <= MESSAGE_SCROLL_BOTTOM_THRESHOLD;
};

const getDeliveryStatusIcon = (message: ChatMessage) => {
  if (!message.fromMe) return null;

  const status = message.deliveryStatus || "pending";

  if (status === "pending") {
    return <Clock3 className="h-3 w-3" />;
  }

  if (status === "sent") {
    return <Check className="h-3 w-3" />;
  }

  const readLike = status === "read" || status === "played";
  return <CheckCheck className={`h-3 w-3 ${readLike ? "text-sky-300" : ""}`} />;
};

const extractMediaResponse = (payload: unknown): { dataUrl: string | null; mimeType: string | null } => {
  if (!isRecord(payload)) {
    return { dataUrl: null, mimeType: null };
  }

  const payloadMimeType =
    typeof payload.mimeType === "string" && payload.mimeType.trim()
      ? payload.mimeType.trim()
      : extractMimeTypeFromPayload(payload.result);

  const topLevelDataUrl = typeof payload.dataUrl === "string" ? payload.dataUrl.trim() : "";
  if (topLevelDataUrl) {
    return { dataUrl: topLevelDataUrl, mimeType: payloadMimeType || null };
  }

  const mediaContent =
    (typeof payload.mediaContent === "string" ? payload.mediaContent.trim() : "") ||
    extractMediaContentFromPayload(payload.result) ||
    extractMediaContentFromPayload(payload);

  const converted = mediaContent ? toMediaDataUrl(mediaContent, payloadMimeType || null) : null;
  return { dataUrl: converted, mimeType: payloadMimeType || null };
};

const formatTime = (isoDate: string | null) => {
  if (!isoDate) return "-";
  return new Date(isoDate).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const normalizeJid = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (trimmed.includes("@")) return trimmed;
  const digits = trimmed.replace(/\D/g, "");
  return digits ? `${digits}@s.whatsapp.net` : trimmed;
};

const numberFromJid = (jid: string) => jid.split("@")[0].replace(/\D/g, "");

const getContactInitials = (contact: Pick<ChatContact, "name" | "number" | "jid">) => {
  const source = contact.name?.trim() || contact.number || contact.jid;
  const words = source
    .split(/\s+/)
    .map((part) => part.trim())
    .filter(Boolean);

  if (words.length >= 2) {
    return `${words[0][0] || ""}${words[1][0] || ""}`.toUpperCase();
  }

  const compact = words[0] || source;
  const alphanumeric = compact.replace(/[^a-zA-Z0-9]/g, "");
  return (alphanumeric.slice(0, 2) || "WA").toUpperCase();
};

const getMessageMediaStateKey = (message: Pick<ChatMessage, "id" | "key">): string => {
  const keyId = message.key?.id?.trim();
  return keyId || message.id;
};

const parseMessageKey = (value: unknown): MessageKey | null => {
  if (!isRecord(value)) return null;

  const id = typeof value.id === "string" ? value.id.trim() : "";
  const remoteJid = typeof value.remoteJid === "string" ? normalizeJid(value.remoteJid) : "";

  if (!id || !remoteJid) return null;

  return {
    id,
    remoteJid,
    fromMe: Boolean(value.fromMe),
    ...(typeof value.participant === "string" ? { participant: value.participant } : {}),
    ...(typeof value.remoteJidAlt === "string" && value.remoteJidAlt.trim()
      ? { remoteJidAlt: normalizeJid(value.remoteJidAlt) }
      : {}),
    ...(typeof value.addressingMode === "string" && value.addressingMode.trim()
      ? { addressingMode: value.addressingMode.trim() }
      : {}),
  };
};

const parseChatsPayload = (payload: unknown): ChatContact[] => {
  if (!isRecord(payload) || !Array.isArray(payload.chats)) return [];

  return payload.chats
    .map((chat) => {
      if (!isRecord(chat) || typeof chat.jid !== "string") {
        return null;
      }

      const jid = normalizeJid(chat.jid);
      if (!jid) return null;

      const number = typeof chat.number === "string" && chat.number.trim() ? chat.number.replace(/\D/g, "") : numberFromJid(jid);

      return {
        jid,
        name:
          typeof chat.name === "string" && chat.name.trim()
            ? chat.name.trim()
            : number
              ? `+${number}`
              : jid,
        number,
        profilePicUrl: typeof chat.profilePicUrl === "string" && chat.profilePicUrl.trim() ? chat.profilePicUrl.trim() : null,
        lastMessage:
          typeof chat.lastMessage === "string" && chat.lastMessage.trim()
            ? chat.lastMessage.trim()
            : null,
        lastTimestamp:
          typeof chat.lastTimestamp === "string" && chat.lastTimestamp.trim()
            ? chat.lastTimestamp.trim()
            : null,
        unreadCount: typeof chat.unreadCount === "number" && Number.isFinite(chat.unreadCount) ? chat.unreadCount : 0,
        isGroup: Boolean(chat.isGroup),
        lastMessageKey: parseMessageKey(chat.lastMessageKey),
      };
    })
    .filter((contact): contact is ChatContact => Boolean(contact));
};

const parseMessagesPayload = (payload: unknown): ChatMessage[] => {
  if (!isRecord(payload) || !Array.isArray(payload.messages)) return [];

  return payload.messages
    .map((message) => {
      if (!isRecord(message) || typeof message.id !== "string" || typeof message.jid !== "string") {
        return null;
      }

      return {
        id: message.id,
        jid: normalizeJid(message.jid),
        fromMe: Boolean(message.fromMe),
        text: typeof message.text === "string" ? message.text : "",
        timestamp: typeof message.timestamp === "string" ? message.timestamp : null,
        hasMedia: Boolean(message.hasMedia),
        mediaType: isMediaType(message.mediaType) ? message.mediaType : null,
        mimeType: typeof message.mimeType === "string" && message.mimeType.trim() ? message.mimeType.trim() : null,
        fileName: typeof message.fileName === "string" && message.fileName.trim() ? message.fileName.trim() : null,
        caption: typeof message.caption === "string" && message.caption.trim() ? message.caption.trim() : null,
        deliveryStatus: isDeliveryStatus(message.deliveryStatus) ? message.deliveryStatus : null,
        statusRaw: typeof message.statusRaw === "string" && message.statusRaw.trim() ? message.statusRaw.trim() : null,
        key: parseMessageKey(message.key),
      };
    })
    .filter((message): message is ChatMessage => Boolean(message) && (Boolean(message.text.trim()) || message.hasMedia));
};

export function WhatsAppChatPanel({
  empresaId,
  isEmpresaLoading,
  instanceName,
  isConnected,
}: WhatsAppChatPanelProps) {
  const [contacts, setContacts] = useState<ChatContact[]>([]);
  const [selectedContactJid, setSelectedContactJid] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [messageInput, setMessageInput] = useState("");
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [contactFilter, setContactFilter] = useState("");
  const [isSyncingMessages, setIsSyncingMessages] = useState(false);
  const [messageMediaById, setMessageMediaById] = useState<Record<string, MessageMediaState>>({});
  const [showScrollToLatest, setShowScrollToLatest] = useState(false);
  const [pendingAttachment, setPendingAttachment] = useState<PendingAttachment | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const messagesPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const selectedContactJidRef = useRef<string | null>(null);
  const messagesScrollAreaRef = useRef<HTMLDivElement | null>(null);
  const messagesViewportRef = useRef<HTMLDivElement | null>(null);
  const shouldAutoScrollRef = useRef(true);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const messageMediaByIdRef = useRef<Record<string, MessageMediaState>>({});
  const mediaLoadInFlightRef = useRef<Set<string>>(new Set());
  const contactsCacheRef = useRef<{
    data: ChatContact[];
    fetchedAt: number;
    empresaId: number | null;
    instanceName: string | null;
  }>({
    data: [],
    fetchedAt: 0,
    empresaId: null,
    instanceName: null,
  });
  const contactsRequestInFlightRef = useRef<Promise<void> | null>(null);

  selectedContactJidRef.current = selectedContactJid;

  const invokeChatAction = useCallback(
    async (body: Record<string, unknown>) => {
      if (!empresaId) {
        throw new Error("Empresa do usuário não identificada");
      }

      const { data, error } = await supabase.functions.invoke("whatsapp-chat", {
        body: {
          empresa_id: empresaId,
          instanceName: instanceName || undefined,
          ...body,
        },
      });

      if (error) {
        const context = (error as Record<string, unknown>).context;
        if (context instanceof Response) {
          try {
            const body = await context.clone().json() as Record<string, unknown>;
            if (typeof body?.error === "string") {
              const details = body.details;
              const detailStr =
                details && typeof details === "object"
                  ? (details as Record<string, unknown>).message ||
                    (details as Record<string, unknown>).error ||
                    JSON.stringify(details)
                  : typeof details === "string"
                    ? details
                    : null;
              throw new Error(detailStr ? `${body.error}: ${detailStr}` : body.error);
            }
          } catch (innerError) {
            if (innerError instanceof Error && innerError.message !== error.message) {
              throw innerError;
            }
          }
        }
        throw new Error(error.message || "Falha na integração de chat");
      }

      return data;
    },
    [empresaId, instanceName],
  );

  const invokeChatActionWithTimeout = useCallback(
    async (body: Record<string, unknown>, timeoutMs = MEDIA_REQUEST_TIMEOUT_MS) => {
      let timeoutId: ReturnType<typeof setTimeout> | null = null;

      try {
        const timeoutPromise = new Promise<never>((_, reject) => {
          timeoutId = setTimeout(() => {
            reject(new Error("Tempo limite ao carregar mídia"));
          }, timeoutMs);
        });

        return await Promise.race([invokeChatAction(body), timeoutPromise]);
      } finally {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      }
    },
    [invokeChatAction],
  );

  const stopMessagesPolling = useCallback(() => {
    if (messagesPollingRef.current) {
      clearInterval(messagesPollingRef.current);
      messagesPollingRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      stopMessagesPolling();
    };
  }, [stopMessagesPolling]);

  useEffect(() => {
    messageMediaByIdRef.current = messageMediaById;
  }, [messageMediaById]);

  useEffect(() => {
    contactsCacheRef.current = {
      data: [],
      fetchedAt: 0,
      empresaId,
      instanceName,
    };
    contactsRequestInFlightRef.current = null;
  }, [empresaId, instanceName]);

  const loadContacts = useCallback(
    async (silent = false, options?: { force?: boolean }) => {
      if (!isConnected || !empresaId || isEmpresaLoading) return;

      const forceRefresh = options?.force === true;
      const cachedContacts = contactsCacheRef.current;
      const hasFreshCache =
        !forceRefresh &&
        cachedContacts.data.length > 0 &&
        cachedContacts.empresaId === empresaId &&
        cachedContacts.instanceName === instanceName &&
        Date.now() - cachedContacts.fetchedAt < CONTACTS_CACHE_TTL_MS;

      if (hasFreshCache) {
        setContacts(cachedContacts.data);

        const selectedJid = selectedContactJidRef.current;
        if (selectedJid && !cachedContacts.data.some((contact) => contact.jid === selectedJid)) {
          setSelectedContactJid(null);
        }

        return;
      }

      if (contactsRequestInFlightRef.current) {
        await contactsRequestInFlightRef.current;
        return;
      }

      if (!silent) {
        setIsLoadingContacts(true);
      }

      const requestPromise = (async () => {
      try {
          const payload = await invokeChatAction({ action: "listChats", forceRefresh });
        const parsedContacts = parseChatsPayload(payload);

          contactsCacheRef.current = {
            data: parsedContacts,
            fetchedAt: Date.now(),
            empresaId,
            instanceName,
          };

        setContacts(parsedContacts);

          const selectedJid = selectedContactJidRef.current;
          if (selectedJid && !parsedContacts.some((contact) => contact.jid === selectedJid)) {
          setSelectedContactJid(null);
        }
      } catch (error) {
        console.error("Erro ao carregar contatos do WhatsApp:", error);
        if (!silent) {
          const message = error instanceof Error ? error.message : "Falha ao carregar contatos";
          toast.error("Erro ao carregar contatos", { description: message });
        }
      } finally {
        if (!silent) {
          setIsLoadingContacts(false);
        }
      }
      })();

      contactsRequestInFlightRef.current = requestPromise;

      try {
        await requestPromise;
      } finally {
        contactsRequestInFlightRef.current = null;
      }
    },
    [empresaId, instanceName, invokeChatAction, isConnected, isEmpresaLoading],
  );

  const loadMessages = useCallback(
    async (contactJid: string, silent = false) => {
      if (!contactJid || !isConnected || !empresaId) return;

      if (!silent) {
        setIsLoadingMessages(true);
      } else {
        setIsSyncingMessages(true);
      }

      try {
        const payload = await invokeChatAction({
          action: "listMessages",
          remoteJid: contactJid,
          offset: 120,
        });

        const parsedMessages = parseMessagesPayload(payload);

        if (selectedContactJidRef.current !== contactJid) {
          return;
        }

        setMessages(parsedMessages);
      } catch (error) {
        console.error("Erro ao carregar mensagens do WhatsApp:", error);
        if (!silent) {
          const message = error instanceof Error ? error.message : "Falha ao carregar mensagens";
          toast.error("Erro ao carregar mensagens", { description: message });
        }
      } finally {
        if (!silent) {
          setIsLoadingMessages(false);
        }
        setIsSyncingMessages(false);
      }
    },
    [empresaId, invokeChatAction, isConnected],
  );

  useEffect(() => {
    if (!isConnected) {
      stopMessagesPolling();
      setContacts([]);
      setMessages([]);
      setSelectedContactJid(null);
      setMessageMediaById({});
      mediaLoadInFlightRef.current.clear();
      contactsCacheRef.current = {
        data: [],
        fetchedAt: 0,
        empresaId,
        instanceName,
      };
      return;
    }

    void loadContacts(false, { force: true });
  }, [empresaId, instanceName, isConnected, loadContacts, stopMessagesPolling]);

  useEffect(() => {
    if (!selectedContactJid || !isConnected) {
      stopMessagesPolling();
      return;
    }

    void loadMessages(selectedContactJid);

    stopMessagesPolling();
    messagesPollingRef.current = setInterval(() => {
      void loadMessages(selectedContactJid, true);
    }, CHAT_POLLING_INTERVAL_MS);

    return () => {
      stopMessagesPolling();
    };
  }, [isConnected, loadMessages, selectedContactJid, stopMessagesPolling]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    setMessageMediaById({});
    mediaLoadInFlightRef.current.clear();
    shouldAutoScrollRef.current = true;
    setShowScrollToLatest(false);
  }, [selectedContactJid]);

  const scrollToLatestMessages = useCallback((behavior: ScrollBehavior = "smooth") => {
    const viewport = messagesViewportRef.current;
    if (!viewport) return;

    viewport.scrollTo({ top: viewport.scrollHeight, behavior });
    shouldAutoScrollRef.current = true;
    setShowScrollToLatest(false);
  }, []);

  useEffect(() => {
    const root = messagesScrollAreaRef.current;
    if (!root) return;

    const viewport = root.querySelector("[data-radix-scroll-area-viewport]");
    if (!(viewport instanceof HTMLDivElement)) return;

    messagesViewportRef.current = viewport;

    const onViewportScroll = () => {
      const nearBottom = isViewportNearBottom(viewport);
      shouldAutoScrollRef.current = nearBottom;
      setShowScrollToLatest(!nearBottom);
    };

    viewport.addEventListener("scroll", onViewportScroll, { passive: true });
    onViewportScroll();

    return () => {
      viewport.removeEventListener("scroll", onViewportScroll);
    };
  }, [selectedContactJid]);

  useEffect(() => {
    if (!messages.length) {
      setShowScrollToLatest(false);
      return;
    }

    const viewport = messagesViewportRef.current;
    if (!viewport) return;

    if (shouldAutoScrollRef.current) {
      const raf = window.requestAnimationFrame(() => {
        scrollToLatestMessages("auto");
      });

      return () => {
        window.cancelAnimationFrame(raf);
      };
    }

    setShowScrollToLatest(!isViewportNearBottom(viewport));
  }, [messages, scrollToLatestMessages]);

  const filteredContacts = useMemo(() => {
    const normalizedFilter = contactFilter.trim().toLowerCase();
    if (!normalizedFilter) return contacts;

    return contacts.filter((contact) => {
      return (
        contact.name.toLowerCase().includes(normalizedFilter) ||
        contact.number.includes(normalizedFilter.replace(/\D/g, ""))
      );
    });
  }, [contactFilter, contacts]);

  const selectedContact = useMemo(() => {
    return contacts.find((contact) => contact.jid === selectedContactJid) || null;
  }, [contacts, selectedContactJid]);

  const latestIncomingMessageKey = useMemo(() => {
    return [...messages].reverse().find((message) => !message.fromMe && message.key)?.key || null;
  }, [messages]);

  const lastActionableKey = latestIncomingMessageKey || selectedContact?.lastMessageKey || null;

  const loadMediaForMessage = useCallback(
    async (message: ChatMessage, options?: { force?: boolean }) => {
      if (!message.hasMedia) return;

      const mediaStateKey = getMessageMediaStateKey(message);
      const mediaMessageId = message.key?.id || message.id;
      if (!mediaMessageId) return;

      const forceRetry = options?.force === true;

      const currentMediaState = messageMediaByIdRef.current[mediaStateKey];
      if (!forceRetry && mediaLoadInFlightRef.current.has(mediaStateKey)) return;
      if (currentMediaState?.dataUrl) return;
      if (!forceRetry && (currentMediaState?.isLoading || currentMediaState?.error)) return;

      const requestedContactJid = selectedContactJidRef.current;
      mediaLoadInFlightRef.current.add(mediaStateKey);
      setMessageMediaById((previous) => ({
        ...previous,
        [mediaStateKey]: {
          isLoading: true,
          dataUrl: previous[mediaStateKey]?.dataUrl ?? null,
          mimeType: previous[mediaStateKey]?.mimeType ?? message.mimeType,
          error: null,
        },
      }));

      try {
        const payload = await invokeChatActionWithTimeout({
          action: "getBase64FromMediaMessage",
          remoteJid: message.jid,
          messageId: mediaMessageId,
          key:
            message.key || {
              id: mediaMessageId,
              remoteJid: message.jid,
              fromMe: message.fromMe,
            },
          convertToMp4: message.mediaType === "video",
          mimeType: message.mimeType || undefined,
        });

        const parsed = extractMediaResponse(payload);
        if (!parsed.dataUrl) {
          throw new Error("Conteúdo de mídia indisponível para renderização.");
        }

        if (selectedContactJidRef.current !== requestedContactJid) {
          return;
        }

        setMessageMediaById((previous) => ({
          ...previous,
          [mediaStateKey]: {
            isLoading: false,
            dataUrl: parsed.dataUrl,
            mimeType: parsed.mimeType || message.mimeType,
            error: null,
          },
        }));
      } catch (error) {
        console.error("Erro ao carregar mídia da mensagem:", error);

        if (selectedContactJidRef.current !== requestedContactJid) {
          return;
        }

        setMessageMediaById((previous) => ({
          ...previous,
          [mediaStateKey]: {
            isLoading: false,
            dataUrl: previous[mediaStateKey]?.dataUrl ?? null,
            mimeType: previous[mediaStateKey]?.mimeType ?? message.mimeType,
            error: error instanceof Error ? error.message : "Falha ao carregar mídia",
          },
        }));
      } finally {
        mediaLoadInFlightRef.current.delete(mediaStateKey);
      }
    },
    [invokeChatActionWithTimeout],
  );

  useEffect(() => {
    if (!isConnected || !selectedContactJid) return;

    const mediaMessages = messages
      .filter((message) => message.hasMedia)
      .slice(-20);

    for (const message of mediaMessages) {
      const mediaStateKey = getMessageMediaStateKey(message);
      if (!messageMediaById[mediaStateKey]) {
        void loadMediaForMessage(message);
      }
    }
  }, [isConnected, loadMediaForMessage, messageMediaById, messages, selectedContactJid]);

  const runChatAction = async (body: Record<string, unknown>, successMessage: string) => {
    if (!selectedContactJid) return;

    try {
      setIsActionLoading(true);
      await invokeChatAction(body);
      toast.success(successMessage);
      await loadMessages(selectedContactJid, true);
      await loadContacts(true, { force: true });
    } catch (error) {
      console.error("Erro em ação de chat:", error);
      const message = error instanceof Error ? error.message : "Falha ao executar ação";
      toast.error("Erro na ação de chat", { description: message });
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!event.target) return;
    (event.target as HTMLInputElement).value = "";
    if (!file) return;

    const mimeType = file.type;
    let mediatype: PendingAttachment["mediatype"];
    if (mimeType.startsWith("image/")) mediatype = "image";
    else if (mimeType.startsWith("video/")) mediatype = "video";
    else if (mimeType.startsWith("audio/")) mediatype = "audio";
    else mediatype = "document";

    const previewUrl = mediatype === "image" ? URL.createObjectURL(file) : null;

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(",")[1] ?? "";
      if (pendingAttachment?.previewUrl) URL.revokeObjectURL(pendingAttachment.previewUrl);
      setPendingAttachment({ base64, mediatype, mimeType, fileName: file.name, previewUrl });
    };
    reader.readAsDataURL(file);
  }, [pendingAttachment]);

  const clearAttachment = useCallback(() => {
    if (pendingAttachment?.previewUrl) URL.revokeObjectURL(pendingAttachment.previewUrl);
    setPendingAttachment(null);
  }, [pendingAttachment]);

  const handleSendMessage = async () => {
    const trimmedMessage = messageInput.trim();

    if (!trimmedMessage && !pendingAttachment) return;
    if (!selectedContactJid) return;

    const optimisticId = `optimistic-${Date.now()}`;
    const mediaLabels: Record<string, string> = { image: "Imagem", video: "Vídeo", audio: "Áudio", document: "Documento" };

    const optimisticMessage: ChatMessage = {
      id: optimisticId,
      jid: selectedContactJid,
      fromMe: true,
      text: trimmedMessage || (pendingAttachment ? `[${mediaLabels[pendingAttachment.mediatype] ?? "Mídia"}]` : ""),
      timestamp: new Date().toISOString(),
      hasMedia: Boolean(pendingAttachment),
      mediaType: pendingAttachment?.mediatype ?? null,
      mimeType: pendingAttachment?.mimeType ?? null,
      fileName: pendingAttachment?.mediatype === "document" ? pendingAttachment.fileName : null,
      caption: pendingAttachment && trimmedMessage ? trimmedMessage : null,
      deliveryStatus: "pending",
      statusRaw: null,
      key: null,
    };

    setMessages((prev) => [...prev, optimisticMessage]);
    setMessageInput("");
    const attachment = pendingAttachment;
    setPendingAttachment(null);
    if (attachment?.previewUrl) URL.revokeObjectURL(attachment.previewUrl);
    shouldAutoScrollRef.current = true;
    setIsSendingMessage(true);

    try {
      if (attachment) {
        await invokeChatAction({
          action: "sendMedia",
          remoteJid: selectedContactJid,
          media: attachment.base64,
          mediatype: attachment.mediatype,
          mimeType: attachment.mimeType,
          caption: trimmedMessage || undefined,
          fileName: attachment.fileName,
        });
      } else {
        await invokeChatAction({
          action: "sendMessage",
          remoteJid: selectedContactJid,
          message: trimmedMessage,
        });
      }

      void loadMessages(selectedContactJid, true).then(() => {
        void loadContacts(false, {});
      });
    } catch (error) {
      setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
      if (!attachment) setMessageInput(trimmedMessage);
      console.error("Erro ao enviar mensagem WhatsApp:", error);
      const message = error instanceof Error ? error.message : "Falha ao enviar mensagem";
      toast.error("Erro ao enviar mensagem", { description: message });
    } finally {
      setIsSendingMessage(false);
    }
  };

  return (
    <Card>
      <CardHeader className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <MessageSquareText className="h-5 w-5 text-slate-700" />
              Ambiente de Chat WhatsApp
            </CardTitle>
            <CardDescription>
              Chats em tempo real com ações operacionais da Evolution API.
            </CardDescription>
          </div>

          <div className="flex items-center gap-2">
            {isSyncingMessages ? (
              <Badge variant="secondary" className="gap-1">
                <Loader2 className="h-3 w-3 animate-spin" /> Sincronizando
              </Badge>
            ) : null}

            <Button
              variant="outline"
              size="sm"
              onClick={() => void loadContacts(false, { force: true })}
              disabled={!isConnected || isLoadingContacts}
            >
              <RefreshCcw className="mr-2 h-4 w-4" /> Atualizar contatos
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {!instanceName ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            Nenhuma instância encontrada. Configure uma instância na
            <Link to="/admin/communication" className="mx-1 underline">
              tela de conexão
            </Link>
            para liberar o chat.
          </div>
        ) : !isConnected ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            Conecte o WhatsApp primeiro para habilitar o chat.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[320px_1fr]">
            <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
              <div className="space-y-2">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-600">Chats</p>
                <Input
                  value={contactFilter}
                  onChange={(event) => setContactFilter(event.target.value)}
                  placeholder="Buscar por nome, número ou grupo"
                />
              </div>

              <ScrollArea className="h-[460px] pr-1">
                <div className="space-y-2">
                  {isLoadingContacts ? (
                    <div className="flex items-center justify-center py-10 text-sm text-slate-500">
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Carregando chats...
                    </div>
                  ) : filteredContacts.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
                      <UsersRound className="mx-auto mb-2 h-5 w-5 text-slate-400" />
                      Nenhum chat retornado pela Evolution API.
                    </div>
                  ) : (
                    filteredContacts.map((contact) => {
                      const isSelected = selectedContactJid === contact.jid;

                      return (
                        <button
                          key={contact.jid}
                          type="button"
                          onClick={() => {
                            shouldAutoScrollRef.current = true;
                            setSelectedContactJid(contact.jid);
                          }}
                          className={`w-full rounded-lg border px-3 py-2 text-left transition-colors ${
                            isSelected
                              ? "border-emerald-300 bg-emerald-50"
                              : "border-slate-200 bg-white hover:border-slate-300"
                          }`}
                        >
                          <div className="flex items-start gap-2">
                            <Avatar className="mt-0.5 h-9 w-9 border border-slate-200 bg-slate-100">
                              <AvatarImage src={contact.profilePicUrl || undefined} alt={contact.name} />
                              <AvatarFallback className="text-[11px] font-semibold text-slate-700">
                                {getContactInitials(contact)}
                              </AvatarFallback>
                            </Avatar>

                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-medium text-slate-800">{contact.name}</p>
                              <p className="truncate text-xs text-slate-500">{contact.number ? `+${contact.number}` : contact.jid}</p>
                              <p className="mt-1 line-clamp-1 text-xs text-slate-500">
                                {contact.lastMessage || "Sem mensagens recentes"}
                              </p>
                              <div className="mt-1 flex items-center justify-between">
                                <p className="text-[10px] text-slate-400">{formatTime(contact.lastTimestamp)}</p>
                                {contact.unreadCount > 0 ? (
                                  <Badge variant="secondary" className="text-[10px]">
                                    {contact.unreadCount} não lidas
                                  </Badge>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </ScrollArea>
            </div>

            <div className="relative flex min-h-[540px] flex-col rounded-xl border border-slate-200 bg-white">
              <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                <div className="flex items-center gap-3">
                  {selectedContact ? (
                    <Avatar className="h-9 w-9 border border-slate-200 bg-slate-100">
                      <AvatarImage src={selectedContact.profilePicUrl || undefined} alt={selectedContact.name} />
                      <AvatarFallback className="text-[11px] font-semibold text-slate-700">
                        {getContactInitials(selectedContact)}
                      </AvatarFallback>
                    </Avatar>
                  ) : null}

                  <div>
                    <p className="text-sm font-semibold text-slate-800">{selectedContact?.name || "Selecione um contato"}</p>
                    <p className="text-xs text-slate-500">
                      {selectedContact?.number ? `+${selectedContact.number}` : "Clique em um contato para abrir o chat"}
                    </p>
                  </div>
                </div>
                {selectedContact ? (
                  <div className="flex flex-wrap justify-end gap-2">
                    {selectedContact.isGroup ? <Badge variant="secondary">Grupo</Badge> : null}
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!lastActionableKey || isActionLoading}
                      onClick={() =>
                        void runChatAction(
                          {
                            action: "markMessagesAsRead",
                            readMessages: [
                              {
                                remoteJid: selectedContact.jid,
                                fromMe: Boolean(lastActionableKey?.fromMe),
                                id: lastActionableKey?.id,
                                participant: lastActionableKey?.participant,
                              },
                            ],
                          },
                          "Mensagens marcadas como lidas",
                        )
                      }
                    >
                      <CheckCheck className="mr-2 h-4 w-4" /> Marcar lidas
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!lastActionableKey || isActionLoading}
                      onClick={() =>
                        void runChatAction(
                          {
                            action: "archiveChat",
                            remoteJid: selectedContact.jid,
                            archive: true,
                            lastMessageKey: lastActionableKey,
                          },
                          "Chat arquivado",
                        )
                      }
                    >
                      <Archive className="mr-2 h-4 w-4" /> Arquivar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={isActionLoading}
                      onClick={() =>
                        void runChatAction(
                          {
                            action: "updateBlockStatus",
                            remoteJid: selectedContact.jid,
                            status: "block",
                          },
                          "Contato bloqueado",
                        )
                      }
                    >
                      <Ban className="mr-2 h-4 w-4" /> Bloquear
                    </Button>
                  </div>
                ) : null}
              </div>

              <ScrollArea ref={messagesScrollAreaRef} className="h-[380px] px-4 py-4">
                {!selectedContact ? (
                  <div className="flex h-full items-center justify-center text-sm text-slate-500">
                    Escolha um contato para visualizar as mensagens.
                  </div>
                ) : isLoadingMessages ? (
                  <div className="flex h-full items-center justify-center text-sm text-slate-500">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Carregando conversa...
                  </div>
                ) : messages.length === 0 ? (
                  <div className="flex h-full items-center justify-center text-sm text-slate-500">
                    Nenhuma mensagem encontrada para este contato.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {messages.map((message) => (
                      <div key={message.id} className={`flex ${message.fromMe ? "justify-end" : "justify-start"}`}>
                        <div
                          className={`max-w-[82%] rounded-xl px-3 py-2 text-sm ${
                            message.fromMe
                              ? "bg-emerald-500 text-white"
                              : "border border-slate-200 bg-slate-100 text-slate-800"
                          }`}
                        >
                          {(() => {
                            const mediaState = messageMediaById[getMessageMediaStateKey(message)];
                            const secondaryTextClass = message.fromMe ? "text-emerald-100" : "text-slate-500";
                            const actionTextClass = message.fromMe
                              ? "text-emerald-50 hover:text-white"
                              : "text-emerald-700 hover:text-emerald-800";
                            const normalizedText = message.text.trim();
                            const hasOnlyPlaceholderText = message.hasMedia && MEDIA_PLACEHOLDER_TEXTS.has(normalizedText);
                            const showMainText = Boolean(normalizedText) && !hasOnlyPlaceholderText;
                            const mediaDataUrl = mediaState?.dataUrl
                              ? patchDataUrlMimeType(
                                  mediaState.dataUrl,
                                  mediaState.mimeType || message.mimeType,
                                  message.mediaType,
                                )
                              : null;

                            return (
                              <>
                                {message.hasMedia ? (
                                  <div className="mb-2 space-y-2">
                                    {mediaState?.isLoading ? (
                                      <div className={`flex items-center gap-2 text-xs ${secondaryTextClass}`}>
                                        <Loader2 className="h-3 w-3 animate-spin" />
                                        Carregando mídia...
                                      </div>
                                    ) : mediaDataUrl ? (
                                      message.mediaType === "image" || message.mediaType === "sticker" ? (
                                        <img
                                          src={mediaDataUrl}
                                          alt={message.fileName || "Imagem recebida"}
                                          className="max-h-64 rounded-md border border-black/10 object-contain"
                                        />
                                      ) : message.mediaType === "video" ? (
                                        <video
                                          controls
                                          src={mediaDataUrl}
                                          className="max-h-64 rounded-md border border-black/10"
                                          preload="metadata"
                                        />
                                      ) : message.mediaType === "audio" ? (
                                        <audio controls src={mediaDataUrl} preload="metadata" className="w-[260px] max-w-full" />
                                      ) : (
                                        <a
                                          href={mediaDataUrl}
                                          download={message.fileName || `arquivo-${message.id}`}
                                          target="_blank"
                                          rel="noreferrer"
                                          className={`inline-flex items-center gap-2 text-xs font-medium underline ${actionTextClass}`}
                                        >
                                          <Download className="h-3 w-3" />
                                          {message.fileName || "Baixar arquivo"}
                                        </a>
                                      )
                                    ) : message.key?.id || message.id ? (
                                      <button
                                        type="button"
                                        className={`inline-flex items-center gap-2 text-xs font-medium underline ${actionTextClass}`}
                                        onClick={() => void loadMediaForMessage(message, { force: true })}
                                      >
                                        <Download className="h-3 w-3" />
                                        Carregar mídia
                                      </button>
                                    ) : (
                                      <p className={`text-xs ${secondaryTextClass}`}>Mídia sem chave disponível.</p>
                                    )}

                                    {mediaState?.error ? (
                                      <div className="space-y-1">
                                        <p className={`text-xs ${secondaryTextClass}`}>Falha ao carregar mídia.</p>
                                        <button
                                          type="button"
                                          className={`inline-flex items-center gap-2 text-xs font-medium underline ${actionTextClass}`}
                                          onClick={() => void loadMediaForMessage(message, { force: true })}
                                        >
                                          Tentar novamente
                                        </button>
                                      </div>
                                    ) : null}
                                  </div>
                                ) : null}

                                {showMainText ? <p className="whitespace-pre-wrap break-words">{message.text}</p> : null}
                              </>
                            );
                          })()}

                          <div className={`mt-1 flex items-center gap-1 text-[10px] ${message.fromMe ? "text-emerald-100" : "text-slate-500"}`}>
                            <span>{formatTime(message.timestamp)}</span>
                            {message.fromMe ? (
                              <span
                                title={message.statusRaw || "Status da mensagem"}
                                className={message.deliveryStatus === "read" || message.deliveryStatus === "played" ? "text-sky-300" : ""}
                              >
                                {getDeliveryStatusIcon(message)}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </ScrollArea>

              {selectedContact && showScrollToLatest ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="absolute bottom-[128px] right-6 z-10 rounded-full shadow-md"
                  onClick={() => scrollToLatestMessages()}
                >
                  <ChevronDown className="mr-1 h-4 w-4" /> Últimas mensagens
                </Button>
              ) : null}

              <Separator />

              <div className="space-y-2 p-4">
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                  onChange={handleFileSelect}
                />

                {pendingAttachment ? (
                  <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                    {pendingAttachment.mediatype === "image" && pendingAttachment.previewUrl ? (
                      <img src={pendingAttachment.previewUrl} alt="preview" className="h-10 w-10 rounded object-cover" />
                    ) : pendingAttachment.mediatype === "audio" ? (
                      <Music className="h-5 w-5 shrink-0 text-slate-500" />
                    ) : (
                      <FileText className="h-5 w-5 shrink-0 text-slate-500" />
                    )}
                    <span className="flex-1 truncate text-xs text-slate-700">{pendingAttachment.fileName}</span>
                    <button
                      type="button"
                      onClick={clearAttachment}
                      className="shrink-0 rounded p-0.5 text-slate-400 hover:text-slate-700"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : null}

                <Textarea
                  value={messageInput}
                  onChange={(event) => setMessageInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && !event.shiftKey) {
                      event.preventDefault();
                      void handleSendMessage();
                    }
                  }}
                  disabled={!selectedContact || isSendingMessage}
                  placeholder={selectedContact ? (pendingAttachment ? "Legenda (opcional)..." : "Digite sua mensagem...") : "Selecione um contato para responder"}
                  className="min-h-[80px]"
                />

                <div className="flex items-center justify-between">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={!selectedContact || isSendingMessage}
                    onClick={() => fileInputRef.current?.click()}
                    className="gap-1.5 text-slate-500 hover:text-slate-700"
                  >
                    <Paperclip className="h-4 w-4" />
                    Anexar
                  </Button>

                  <Button
                    onClick={() => void handleSendMessage()}
                    disabled={!selectedContact || isSendingMessage || (!messageInput.trim() && !pendingAttachment)}
                    className="gap-2"
                  >
                    {isSendingMessage ? <Loader2 className="h-4 w-4 animate-spin" /> : <SendHorizontal className="h-4 w-4" />}
                    Enviar mensagem
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
